import { useState, useRef } from "react";
import {
  Dialog,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/label";
import { Plus, Camera, X, Briefcase } from "lucide-react";
import { cn } from "src/utils/Functions";
import { usePortfolio, type CreatePortfolioEntityData } from "@/contexts/PortfolioContext";
import { COLOR_PRESETS, getIconForType } from "@/constants/portfolio.constants";

export function CreatePortfolioDialog() {
  const { isCreateDialogOpen, closeCreateDialog, addEntity } = usePortfolio();

  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [colorIdx, setColorIdx] = useState(0);
  const [image, setImage] = useState<string | undefined>(undefined);
  const [nameError, setNameError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setName("");
    setType("");
    setColorIdx(0);
    setImage(undefined);
    setNameError("");
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      closeCreateDialog();
      resetForm();
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result as string);
    };
    reader.readAsDataURL(file);
    // Reset file input so re-selecting the same file triggers onChange
    e.target.value = "";
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setNameError("Name is required");
      return;
    }

    const data: CreatePortfolioEntityData = {
      name: name.trim(),
      type: type.trim() || "other",
      colorPreset: COLOR_PRESETS[colorIdx],
      image,
    };

    addEntity(data);
    resetForm();
    closeCreateDialog();
  };

  const selectedColor = COLOR_PRESETS[colorIdx];
  const PreviewIcon = getIconForType(type.trim());

  return (
    <Dialog open={isCreateDialogOpen} onOpenChange={handleOpenChange}>
      <DialogPopup className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Plus className="size-5 text-muted-foreground" />
            New Portfolio Entity
          </DialogTitle>
          <DialogDescription>
            Add a new entity to your portfolio.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Picture upload */}
          <div className="flex flex-col items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className={cn(
                "relative size-20 rounded-full grid place-items-center transition-all border-2 border-dashed overflow-hidden group",
                image
                  ? "border-transparent"
                  : "border-muted-foreground/30 hover:border-muted-foreground/60"
              )}
            >
              {image ? (
                <>
                  <img
                    src={image}
                    alt="Entity avatar"
                    className="size-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                    <Camera className="size-5 text-white" />
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                  <Camera className="size-5" />
                  <span className="text-[10px]">Upload</span>
                </div>
              )}
            </button>
            {image && (
              <button
                type="button"
                onClick={() => setImage(undefined)}
                className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
              >
                <X className="size-3" />
                Remove
              </button>
            )}
          </div>

          {/* Name */}
          <div className="space-y-1.5">
            <Label htmlFor="entity-name" className="text-sm font-medium">
              Name
            </Label>
            <Input
              id="entity-name"
              type="text"
              placeholder="e.g. Kendrick Lamar"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError("");
              }}
              autoComplete="off"
              className={cn(nameError && "border-destructive")}
            />
            {nameError && (
              <p className="text-destructive text-xs">{nameError}</p>
            )}
          </div>

          {/* Type — free text */}
          <div className="space-y-1.5">
            <Label htmlFor="entity-type" className="text-sm font-medium">
              Type <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="entity-type"
              type="text"
              placeholder="e.g. Artist, Brand, Label, Fund…"
              value={type}
              onChange={(e) => setType(e.target.value)}
              autoComplete="off"
            />
          </div>

          {/* Color */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Color</Label>
            <div className="flex items-center gap-2">
              {COLOR_PRESETS.map((preset, idx) => (
                <button
                  key={preset.color}
                  type="button"
                  onClick={() => setColorIdx(idx)}
                  className={cn(
                    "size-7 rounded-full transition-all shrink-0",
                    preset.swatch,
                    idx === colorIdx
                      ? "ring-2 ring-offset-2 ring-offset-background ring-current scale-110"
                      : "opacity-60 hover:opacity-100"
                  )}
                  aria-label={preset.color.replace("text-", "").replace("-500", "")}
                />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-xl border border-border p-3 flex items-center gap-3">
            <div className={cn("size-10 rounded-full grid place-items-center shrink-0 overflow-hidden", !image && selectedColor.bgClass)}>
              {image ? (
                <img src={image} alt="" className="size-full object-cover" />
              ) : (
                <PreviewIcon className={cn("size-5", selectedColor.color)} />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground truncate">
                {name.trim() || "Entity Name"}
              </div>
              <div className="text-xs text-muted-foreground capitalize">
                {type.trim() || "Portfolio"} · 0 deals · $0
              </div>
            </div>
          </div>

          <DialogFooter>
            <DialogClose
              render={
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              }
            />
            <Button type="submit">
              <Plus className="size-3 mr-0.5" />
              Create Entity
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}

export default CreatePortfolioDialog;
